import React, { useState, useEffect, useCallback } from "react";
import { Navigate } from "react-router-dom";
import UserService from "../services/user.service";
import EventBus from "../common/EventBus";
import { Table, Button, Container, Modal, Form, ButtonGroup, Pagination, Spinner } from 'react-bootstrap';
import { FaEdit, FaTrash } from 'react-icons/fa';
import { useSelector } from "react-redux";
import ErrorModal from './ErrorModal';

const resellerText = (value) => (value == null ? "" : String(value));

const RESELLER_TEXT_FIELDS = [
  "company_name",
  "company_mobile_number",
  "company_email",
  "contact_first_name",
  "contact_last_name",
  "contact_mobile_number",
  "contact_email",
  "street",
  "city",
  "state",
  "postal_code",
  "country",
  "bank_name",
  "bank_account_number",
  "bank_swift_code",
  "bank_iban",
  "bank_address",
  "company_registry",
  "company_court",
  "company_ceo",
  "company_tax_number",
  "company_vat_id",
  "street_add",
  "city_add",
  "street_number",
];

const normalizeResellerForm = (record) => {
  const next = { ...(record || {}) };
  RESELLER_TEXT_FIELDS.forEach((key) => {
    if (next[key] == null) next[key] = "";
  });
  if (!next.t_main_vo) {
    next.t_main_vo = { main_vo_id: 0, company_name: "" };
  } else if (next.t_main_vo.company_name == null) {
    next.t_main_vo = { ...next.t_main_vo, company_name: "" };
  }
  return next;
};

const BoardSubVos = () => {
  const { user: currentUser } = useSelector((state) => state.auth);
  const isAdminUser = Boolean(currentUser?.roles?.includes("ROLE_ADMIN"));
  const isMainVoUser = Boolean(currentUser?.roles?.includes("ROLE_MAINVO"));
  const canManageResellers = isAdminUser || isMainVoUser;
  const [mainvos, setMainVos] = useState([]);
  const [subvos, setSubVos] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedSubVos, setSelectedSubVos] = useState(null);
  const [deleteState, setDeleteState] = useState(false);
  const [errors, setErrors] = useState({});
  const [apierrors, setApiErrors] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  
  // State for showing details modal
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedVoForDetails, setSelectedVoForDetails] = useState(null);

  const [filterTerm, setFilterTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  
  const [loading, setLoading] = useState(false);
  const [loadingText, setLoadingText] = useState(' Lade, bitte warten...');
  const [showModal, setShowModal] = useState(false);
  const [errorCode, setApiErrorCode] = useState('');

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
    }
  };

  const [formData, setFormData] = useState({
    company_name: "",
    company_mobile_number: "",
    company_email: "",
    contact_first_name: "",
    contact_last_name: "",
    contact_mobile_number: "",
    contact_email: "",
    street: "",
    city: "",
    state: "",
    postal_code: "",
    country: "",
    bank_name: "",
    bank_account_number: "",
    bank_swift_code: "",
    bank_iban: "",
    bank_address: "",
    company_registry: "",
    company_court: "",
    company_ceo: "",
    company_tax_number: "",
    company_vat_id: "",
    street_add: "",
    city_add: "",
    street_number: "",
    t_main_vo: { main_vo_id: 0, company_name: "" }
  });

  const validateForm = useCallback(() => {
    const newErrors = {};
  
    if (!resellerText(formData.company_name).trim()) {
      newErrors.company_name = 'Firmenname ist ein Pflichtfeld';
    }
  
    if (!resellerText(formData.company_mobile_number).trim()) {
      newErrors.company_mobile_number = 'Firmen-Mobilnummer ist ein Pflichtfeld';
    } else if (!/^(00\d{2,3}|0\d{4,}|(\+49)?\d{3,})\d{6,}$/g.test(formData.company_mobile_number)) {
      newErrors.company_mobile_number = 'Firmen-Mobilnummer muss eine gültige Telefonnummer sein';
    }
  
    if (!resellerText(formData.company_email).trim()) {
      newErrors.company_email = 'Firmen-E-Mail(Benutzername) ist ein Pflichtfeld';
    } else if (!/\S+@\S+\.\S+/.test(formData.company_email)) {
      newErrors.company_email = 'Firmen-E-Mail(Benutzername) muss eine gültige E-Mail-Adresse sein';
    }
  
    if (!resellerText(formData.contact_first_name).trim()) {
      newErrors.contact_first_name = 'Kontakt Vorname ist ein Pflichtfeld';
    }
  
    if (!resellerText(formData.contact_last_name).trim()) {
      newErrors.contact_last_name = 'Kontakt Nachname ist ein Pflichtfeld';
    }
  
    if (!resellerText(formData.contact_mobile_number).trim()) {
      newErrors.contact_mobile_number = 'Kontakt Mobilnummer ist ein Pflichtfeld';
    } else if (!/^(00\d{2,3}|0\d{4,}|(\+49)?\d{3,})\d{6,}$/g.test(formData.contact_mobile_number)) {
      newErrors.contact_mobile_number = 'Kontakt Mobilnummer muss eine gültige internationale Telefonnummer sein';
    }
  
    if (!resellerText(formData.contact_email).trim()) {
      newErrors.contact_email = 'Kontakt E-Mail ist ein Pflichtfeld';
    } else if (!/\S+@\S+\.\S+/.test(formData.contact_email)) {
      newErrors.contact_email = 'Kontakt E-Mail muss eine gültige E-Mail-Adresse sein';
    }

    if (currentUser?.roles?.includes("ROLE_ADMIN")) {
      if (!formData.t_main_vo?.main_vo_id || isNaN(formData.t_main_vo?.main_vo_id) || parseInt(formData.t_main_vo?.main_vo_id) <= 0) {
        newErrors.main_vo_id = 'Eine gültige Haupt-VO muss ausgewählt werden.';
      }
    }

    if (formData.street && !formData.street.trim()) newErrors.street = 'Straße ist erforderlich, wenn angegeben';
    if (formData.city && !formData.city.trim()) newErrors.city = 'Stadt ist erforderlich, wenn angegeben';
    if (formData.state && !formData.state.trim()) newErrors.state = 'Bundesland ist erforderlich, wenn angegeben';
    if (formData.postal_code && !/^\d{4,10}$/.test(formData.postal_code)) newErrors.postal_code = 'PLZ muss eine gültige Zahl sein';
    if (formData.country && !formData.country.trim()) newErrors.country = 'Land ist erforderlich, wenn angegeben';
    if (formData.bank_name && !formData.bank_name.trim()) newErrors.bank_name = 'Bankname ist erforderlich, wenn angegeben';
    if (formData.bank_account_number && !/^\d{8,20}$/.test(formData.bank_account_number)) newErrors.bank_account_number = 'Kontonummer muss 8-20 Ziffern haben';
    if (formData.bank_swift_code && !/^[A-Z0-9]{8,11}$/.test(formData.bank_swift_code)) newErrors.bank_swift_code = 'Ungültiger SWIFT-Code';
    if (formData.bank_iban && !/^[A-Z0-9]{15,34}$/.test(formData.bank_iban)) newErrors.bank_iban = 'Ungültige IBAN';
    if (formData.bank_address && !formData.bank_address.trim()) newErrors.bank_address = 'Bankadresse ist erforderlich, wenn angegeben';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, currentUser, setErrors]);

  useEffect(() => {
    if (!canManageResellers) {
      setMainVos([]);
      setSubVos([]);
      return;
    }

    if (isAdminUser) {
      UserService.getMainVosBoard().then(
        (response) => {
          setMainVos(Array.isArray(response.data) ? response.data : []);
        }).catch((error) => {
          if (error.response) {
            setApiErrors(`Fehler: ${error.name} - ${error.response.data.message}-${error.code}`);
            setApiErrorCode(`Fehler: ${error.name} - ${error.code}`);
          } else if (error.request) {
            setApiErrors('Fehler: Keine Antwort vom Server erhalten.');
            setApiErrorCode(`Fehler: ${error.name} - ${error.code}`);
          } else {
            setApiErrors(`Fehler: ${error.message}`);
            setApiErrorCode(`Fehler: ${error.name} - ${error.code}`);
          }
          setMainVos([]);
          if (error.response?.status === 401) {
            EventBus.dispatch("logout");
          }
      });
    } else {
      setMainVos([]);
    }

    UserService.getSubVosBoard().then(
      (response) => {
        setSubVos(Array.isArray(response.data) ? response.data : []);
      },
      (error) => {
        if (error.response) {
          setApiErrors(`Fehler: ${error.name} - ${error.response.data.message}-${error.code}`);
          setApiErrorCode(`Fehler: ${error.name} - ${error.code}`);
        } else if (error.request) {
          setApiErrors('Fehler: Keine Antwort vom Server erhalten.');
          setApiErrorCode(`Fehler: ${error.name} - ${error.code}`);
        } else {
          setApiErrors(`Fehler: ${error.message}`);
          setApiErrorCode(`Fehler: ${error.name} - ${error.code}`);
        }
        setSubVos([]);
        if (error.response?.status === 401) {
          EventBus.dispatch("logout");
        }
      }
    );
  }, [deleteState, canManageResellers, isAdminUser]);

  useEffect(() => {
    validateForm();
  }, [formData, validateForm]);

  const filteredMainVOs = Array.isArray(subvos)
    ? subvos.filter((vo) => {
        const term = filterTerm.toLowerCase();
        return (
            (vo.company_name && vo.company_name.toLowerCase().includes(term)) ||
            (vo.contact_first_name && vo.contact_first_name.toLowerCase().includes(term)) ||
            (vo.contact_last_name && vo.contact_last_name.toLowerCase().includes(term)) ||
            (vo.company_email && vo.company_email.toLowerCase().includes(term)) ||
            (vo.city && vo.city.toLowerCase().includes(term)) ||
            (String(vo.sub_vo_id).includes(term))
        );
    }) : [];

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedMainVOs = [...filteredMainVOs].sort((a, b) => {
    let aValue, bValue;
    if (sortConfig.key === 'contact_last_name') {
        aValue = `${a.contact_first_name || ''} ${a.contact_last_name || ''}`.trim();
        bValue = `${b.contact_first_name || ''} ${b.contact_last_name || ''}`.trim();
    } else if (sortConfig.key === 't_main_vo.company_name') {
      aValue = a.t_main_vo?.company_name || '';
      bValue = b.t_main_vo?.company_name || '';
    } else {
      aValue = a[sortConfig.key] || '';
      bValue = b[sortConfig.key] || '';
    }
    
    if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentMainVOs = sortedMainVOs.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(sortedMainVOs.length / itemsPerPage);

  const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);

  const handleEditClick = (sub_vo) => {
    setApiErrors(null);
    setApiErrorCode(null);
    setSelectedSubVos(sub_vo);
    setFormData(normalizeResellerForm(sub_vo));
    setShowEditModal(true);
  };
  
  const handleDetailsClick = (vo) => {
      setSelectedVoForDetails(vo);
      setShowDetailsModal(true);
  };

  const handleCloseDetailsModal = () => {
      setShowDetailsModal(false);
      setSelectedVoForDetails(null);
  };

  const handleCreateClick = () => {
    setApiErrors(null);
    setApiErrorCode(null);
    setFormData({
        company_name: "",
        company_mobile_number: "",
        company_email: "",
        contact_first_name: "",
        contact_last_name: "",
        contact_mobile_number: "",
        contact_email: "",
        street: "",
        city: "",
        state: "",
        postal_code: "",
        country: "",
        bank_name: "",
        bank_account_number: "",
        bank_swift_code: "",
        bank_iban: "",
        bank_address: "",
        company_registry: "",
        company_court: "",
        company_ceo: "",
        company_tax_number: "",
        company_vat_id: "",
        street_add: "",
        city_add: "",
        street_number: "",
        t_main_vo: { main_vo_id: 0, company_name: "" }
    });
    setLogoFile(null);
    setShowCreateModal(true);
  };

  // const isValidDate = (date) => !isNaN(Date.parse(date));

  const handleDeleteClick = async (id, e) => {
    e.preventDefault();
    setApiErrors(null);
    setApiErrorCode(null);
    if (window.confirm('Sind Sie sicher, dass Sie diesen Eintrag löschen möchten?')) {
        try {
            setLoadingText('Daten werden gelöscht, bitte warten...');
            setLoading(true);
            await UserService.deleteSubVosBoard(id);
            setDeleteState(prev => !prev);
        } catch (error) {
            setApiErrors(`Fehler: ${error.name}-${error.code} - ${error.response?.data?.message}-${error.response?.data?.error}`);
            setApiErrorCode(`Fehler: ${error.name} - ${error.code}`);
            setShowModal(true);
        } finally {
            setLoading(false);
        }
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    // Erstelle ein sauberes Datenobjekt für das Backend
    const updateData = {
      ...formData, // Nimm alle Felder aus dem Formular
      main_vo_id: formData.main_vo?.main_vo_id // Korrekter Name
    };

    // Entferne das nun überflüssige verschachtelte Objekt, damit es nicht gesendet wird
    delete updateData.main_vo; // Korrekter Name

    try {
        setLoadingText('Daten werden aktualisiert, bitte warten...');
        setLoading(true);
        // Sende die korrigierten Daten
        await UserService.updateSubVosBoard(selectedSubVos.sub_vo_id, updateData);
        setDeleteState(prev => !prev);
        setShowEditModal(false);
    } catch (error) {
        setApiErrors(`Fehler: ${error.name} - ${error.response?.data?.message}-${error.code}`);
        setApiErrorCode(`Fehler: ${error.name} - ${error.code}`);
        setShowModal(true);
    } finally {
        setLoading(false);
    }
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    try {
        setLoadingText('Daten werden erstellt, bitte warten...');
        setLoading(true);
        
        const formDataWithLogo = new FormData();
        
        Object.keys(formData).forEach(key => {
            const value = formData[key];
            if (key === 't_main_vo' && typeof value === 'object' && value !== null) {
                formDataWithLogo.append('t_main_vo.main_vo_id', value.main_vo_id);
            } else if (value !== null && value !== undefined) {
                formDataWithLogo.append(key, value);
            }
        });

        if (logoFile) {
            formDataWithLogo.append('logo', logoFile);
        }
        
        await UserService.createSubVosBoard(formDataWithLogo);
        setDeleteState(prev => !prev);
        setShowCreateModal(false);
    } catch (error) {
        setApiErrors(`Fehler: ${error.name} - ${error.response?.data?.message}-${error.code}`);
        setApiErrorCode(`Fehler: ${error.name} - ${error.code}`);
        setShowModal(true);
    } finally {
        setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (selectedSubVos && window.confirm(`Sind Sie sicher, dass Sie das Passwort für ${selectedSubVos.company_name} zurücksetzen möchten? Der Benutzer wird per E-Mail benachrichtigt.`)) {
      try {
        setLoadingText('Passwort wird zurückgesetzt, bitte warten...');
        setLoading(true);
        const response = await UserService.resetSubVoPassword(selectedSubVos.sub_vo_id);
        alert(response.data.message); // Einfache Erfolgsmeldung
        setShowEditModal(false);
      } catch (error) {
        setApiErrors(`Fehler: ${error.name} - ${error.response?.data?.message}-${error.code}`);
        setApiErrorCode(`Fehler: ${error.name} - ${error.code}`);
        setShowModal(true);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleBlur = () => validateForm();

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "main_vo_id") {
        setFormData(prevState => ({
            ...prevState,
            t_main_vo: { ...prevState.t_main_vo, main_vo_id: value }
        }));
    } else {
        setFormData(prevState => ({ ...prevState, [name]: value }));
    }
  };

  const handleCloseModal = () => setShowModal(false);

  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  return (
    <Container>
      <h1 className="mt-5">Wiederverkäufer Übersicht</h1>
      <ErrorModal show={showModal} handleClose={handleCloseModal} errorCode={errorCode} errorMessage={apierrors} />

      {!canManageResellers && (
        <p className="mt-3">
          Diese Übersicht ist für die Haupt-Vertriebsorganisation. Mit diesem Konto werden hier keine Wiederverkäufer geladen.
        </p>
      )}

      {canManageResellers && (
      <Button variant="info" onClick={handleCreateClick} className="mr-2 mb-2">
        Erstellen
      </Button>
      )}
      
      {loading && (
        <div className="spinner-overlay">
          <div className="spinner-background">
            <Spinner animation="border" role="status" className="spinner-border">
              <span className="sr-only">Lade...</span>
            </Spinner>
            <div className="spinner-text">...{loadingText}</div>
          </div>
        </div>
      )}

      <Form.Group controlId="formBasicSearch" className="mb-3">
        <Form.Control
          type="text"
          placeholder="Suchen..."
          value={filterTerm}
          onChange={(e) => setFilterTerm(e.target.value)}
        />
      </Form.Group>

      <Table striped bordered hover responsive className="mt-2">
        <thead>
          <tr>
            <th onClick={() => handleSort('sub_vo_id')} style={{ cursor: 'pointer' }}>
              ID {sortConfig.key === 'sub_vo_id' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
            </th>
            <th onClick={() => handleSort('company_name')} style={{ cursor: 'pointer' }}>
              Firmenname {sortConfig.key === 'company_name' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
            </th>
            <th onClick={() => handleSort('contact_last_name')} style={{ cursor: 'pointer' }}>
              Ansprechpartner {sortConfig.key === 'contact_last_name' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
            </th>
            <th onClick={() => handleSort('company_email')} style={{ cursor: 'pointer' }}>
              Firmen-E-Mail(Benutzername) {sortConfig.key === 'company_email' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
            </th>
            <th onClick={() => handleSort('city')} style={{ cursor: 'pointer' }}>
              Stadt {sortConfig.key === 'city' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
            </th>
            <th>Aktionen</th>
          </tr>
        </thead>
        <tbody>
          {currentMainVOs.map((vo) => (
            <tr key={vo.sub_vo_id}>
              <td>{vo.sub_vo_id}</td>
              <td>{vo.company_name}</td>
              <td>{`${vo.contact_first_name || ''} ${vo.contact_last_name || ''}`}</td>
              <td>{vo.company_email}</td>
              <td>{vo.city}</td>
              <td>
                <ButtonGroup>
                  <Button variant="secondary" onClick={() => handleDetailsClick(vo)} className="mr-2">
                    Details
                  </Button>
                  <Button variant="warning" onClick={() => handleEditClick(vo)} className="mr-2" title="Bearbeiten" aria-label="Bearbeiten">
                    <FaEdit />
                  </Button>
                  <Button variant="danger" onClick={(e) => handleDeleteClick(vo.sub_vo_id, e)} title="Löschen" aria-label="Löschen">
                    <FaTrash />
                  </Button>
                </ButtonGroup>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      
      <Pagination>
        <Pagination.First onClick={() => handlePageChange(1)} disabled={currentPage === 1} />
        <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
        {[...Array(totalPages)].map((_, index) => (
          <Pagination.Item key={index} active={index + 1 === currentPage} onClick={() => handlePageChange(index + 1)}>
            {index + 1}
          </Pagination.Item>
        ))}
        <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
        <Pagination.Last onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} />
      </Pagination>

      {/* Create Modal */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Neuen Wiederverkäufer erstellen</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleCreateSubmit}>
            <Form.Group className="mb-2" controlId="logoFile">
              <Form.Label>Kundenlogo</Form.Label>
              <Form.Control type="file" accept="image/*" onChange={handleFileChange} />
            </Form.Group>
            <Form.Group className="mb-2" controlId="formCompanyName">
              <Form.Label>Firmenname</Form.Label>
              <Form.Control type="text" placeholder="Firmenname eingeben" name="company_name" value={formData.company_name} onChange={handleChange} onBlur={handleBlur} isInvalid={!!errors.company_name} />
              <Form.Control.Feedback type="invalid">{errors.company_name}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-2" controlId="formCompanyMobile">
              <Form.Label>Firmen-Mobilnummer</Form.Label>
              <Form.Control type="text" placeholder="Mobilnummer der Firma eingeben" name="company_mobile_number" value={formData.company_mobile_number} onChange={handleChange} onBlur={handleBlur} isInvalid={!!errors.company_mobile_number} />
              <Form.Control.Feedback type="invalid">{errors.company_mobile_number}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-2" controlId="formCompanyEmail">
              <Form.Label>Firmen-E-Mail(Benutzername)</Form.Label>
              <Form.Control type="email" placeholder="E-Mail der Firma eingeben" name="company_email" value={formData.company_email} onChange={handleChange} onBlur={handleBlur} isInvalid={!!errors.company_email} />
              <Form.Control.Feedback type="invalid">{errors.company_email}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-2" controlId="formContactFirstName">
              <Form.Label>Kontakt Vorname</Form.Label>
              <Form.Control type="text" placeholder="Vornamen des Kontakts eingeben" name="contact_first_name" value={formData.contact_first_name} onChange={handleChange} onBlur={handleBlur} isInvalid={!!errors.contact_first_name} />
              <Form.Control.Feedback type="invalid">{errors.contact_first_name}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-2" controlId="formContactLastName">
              <Form.Label>Kontakt Nachname</Form.Label>
              <Form.Control type="text" placeholder="Nachnamen des Kontakts eingeben" name="contact_last_name" value={formData.contact_last_name} onChange={handleChange} onBlur={handleBlur} isInvalid={!!errors.contact_last_name} />
              <Form.Control.Feedback type="invalid">{errors.contact_last_name}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-2" controlId="formContactMobile">
              <Form.Label>Kontakt Mobilnummer</Form.Label>
              <Form.Control type="text" placeholder="Mobilnummer des Kontakts eingeben" name="contact_mobile_number" value={formData.contact_mobile_number} onChange={handleChange} onBlur={handleBlur} isInvalid={!!errors.contact_mobile_number} />
              <Form.Control.Feedback type="invalid">{errors.contact_mobile_number}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-2" controlId="formContactEmail">
              <Form.Label>Kontakt E-Mail</Form.Label>
              <Form.Control type="email" placeholder="E-Mail des Kontakts eingeben" name="contact_email" value={formData.contact_email} onChange={handleChange} onBlur={handleBlur} isInvalid={!!errors.contact_email} />
              <Form.Control.Feedback type="invalid">{errors.contact_email}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-2" controlId="formStreet">
              <Form.Label>Straße</Form.Label>
              <Form.Control type="text" placeholder="Straße eingeben" name="street" value={formData.street} onChange={handleChange} onBlur={handleBlur} isInvalid={!!errors.street} />
              <Form.Control.Feedback type="invalid">{errors.street}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-2" controlId="formStreetNumber">
              <Form.Label>Hausnummer</Form.Label>
              <Form.Control type="text" placeholder="Hausnummer eingeben" name="street_number" value={formData.street_number} onChange={handleChange} onBlur={handleBlur} isInvalid={!!errors.street_number} />
              <Form.Control.Feedback type="invalid">{errors.street_number}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-2" controlId="formStreetAdd">
              <Form.Label>Straßenzusatz</Form.Label>
              <Form.Control type="text" placeholder="Straßenzusatz eingeben" name="street_add" value={formData.street_add} onChange={handleChange} onBlur={handleBlur} isInvalid={!!errors.street_add} />
              <Form.Control.Feedback type="invalid">{errors.street_add}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-2" controlId="formCity">
              <Form.Label>Stadt</Form.Label>
              <Form.Control type="text" placeholder="Stadt eingeben" name="city" value={formData.city} onChange={handleChange} onBlur={handleBlur} isInvalid={!!errors.city} />
              <Form.Control.Feedback type="invalid">{errors.city}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-2" controlId="formCityAdd">
              <Form.Label>Stadt Zusatz</Form.Label>
              <Form.Control type="text" placeholder="Stadt Zusatz eingeben" name="city_add" value={formData.city_add} onChange={handleChange} onBlur={handleBlur} isInvalid={!!errors.city_add} />
              <Form.Control.Feedback type="invalid">{errors.city_add}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-2" controlId="formPostalCode">
              <Form.Label>PLZ</Form.Label>
              <Form.Control type="text" placeholder="PLZ eingeben" name="postal_code" value={formData.postal_code} onChange={handleChange} onBlur={handleBlur} isInvalid={!!errors.postal_code} />
              <Form.Control.Feedback type="invalid">{errors.postal_code}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-2" controlId="formState">
              <Form.Label>Bundesland</Form.Label>
              <Form.Control type="text" placeholder="Bundesland eingeben" name="state" value={formData.state} onChange={handleChange} onBlur={handleBlur} isInvalid={!!errors.state} />
              <Form.Control.Feedback type="invalid">{errors.state}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-2" controlId="formCountry">
              <Form.Label>Land</Form.Label>
              <Form.Control type="text" placeholder="Land eingeben" name="country" value={formData.country} onChange={handleChange} onBlur={handleBlur} isInvalid={!!errors.country} />
              <Form.Control.Feedback type="invalid">{errors.country}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-2" controlId="formBankName">
              <Form.Label>Bankname</Form.Label>
              <Form.Control type="text" placeholder="Bankname eingeben" name="bank_name" value={formData.bank_name} onChange={handleChange} onBlur={handleBlur} isInvalid={!!errors.bank_name} />
              <Form.Control.Feedback type="invalid">{errors.bank_name}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-2" controlId="formBankAccountNumber">
              <Form.Label>Kontonummer</Form.Label>
              <Form.Control type="text" placeholder="Kontonummer eingeben" name="bank_account_number" value={formData.bank_account_number} onChange={handleChange} onBlur={handleBlur} isInvalid={!!errors.bank_account_number} />
              <Form.Control.Feedback type="invalid">{errors.bank_account_number}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-2" controlId="formBankSwiftCode">
              <Form.Label>SWIFT-Code</Form.Label>
              <Form.Control type="text" placeholder="SWIFT-Code eingeben" name="bank_swift_code" value={formData.bank_swift_code} onChange={handleChange} onBlur={handleBlur} isInvalid={!!errors.bank_swift_code} />
              <Form.Control.Feedback type="invalid">{errors.bank_swift_code}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-2" controlId="formBankIBAN">
              <Form.Label>IBAN</Form.Label>
              <Form.Control type="text" placeholder="IBAN eingeben" name="bank_iban" value={formData.bank_iban} onChange={handleChange} onBlur={handleBlur} isInvalid={!!errors.bank_iban} />
              <Form.Control.Feedback type="invalid">{errors.bank_iban}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-2" controlId="formBankAddress">
              <Form.Label>Bankadresse</Form.Label>
              <Form.Control type="text" placeholder="Bankadresse eingeben" name="bank_address" value={formData.bank_address} onChange={handleChange} onBlur={handleBlur} isInvalid={!!errors.bank_address} />
              <Form.Control.Feedback type="invalid">{errors.bank_address}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-2" controlId="formCompanyRegistry">
              <Form.Label>Handelsregister</Form.Label>
              <Form.Control type="text" placeholder="Handelsregister eingeben" name="company_registry" value={formData.company_registry} onChange={handleChange} onBlur={handleBlur} isInvalid={!!errors.company_registry} />
              <Form.Control.Feedback type="invalid">{errors.company_registry}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-2" controlId="formCompanyCourt">
              <Form.Label>Registergericht</Form.Label>
              <Form.Control type="text" placeholder="Registergericht eingeben" name="company_court" value={formData.company_court} onChange={handleChange} onBlur={handleBlur} isInvalid={!!errors.company_court} />
              <Form.Control.Feedback type="invalid">{errors.company_court}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-2" controlId="formCompanyCEO">
              <Form.Label>Geschäftsführer</Form.Label>
              <Form.Control type="text" placeholder="Geschäftsführer eingeben" name="company_ceo" value={formData.company_ceo} onChange={handleChange} onBlur={handleBlur} isInvalid={!!errors.company_ceo} />
              <Form.Control.Feedback type="invalid">{errors.company_ceo}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-2" controlId="formCompanyTaxNumber">
              <Form.Label>Steuernummer</Form.Label>
              <Form.Control type="text" placeholder="Steuernummer eingeben" name="company_tax_number" value={formData.company_tax_number} onChange={handleChange} onBlur={handleBlur} isInvalid={!!errors.company_tax_number} />
              <Form.Control.Feedback type="invalid">{errors.company_tax_number}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-2" controlId="formCompanyVATID">
              <Form.Label>Umsatzsteuer-ID</Form.Label>
              <Form.Control type="text" placeholder="Umsatzsteuer-ID eingeben" name="company_vat_id" value={formData.company_vat_id} onChange={handleChange} onBlur={handleBlur} isInvalid={!!errors.company_vat_id} />
              <Form.Control.Feedback type="invalid">{errors.company_vat_id}</Form.Control.Feedback>
            </Form.Group>
            {currentUser?.roles?.includes("ROLE_ADMIN") && (
            <Form.Group className="mb-2" controlId="formSubVo">
              <Form.Label>Haupt-VO auswählen</Form.Label>
              <Form.Control as="select" name="main_vo_id" value={formData.t_main_vo.main_vo_id} onChange={handleChange} onBlur={handleBlur} isInvalid={!!errors.main_vo_id}>
                <option value="">Haupt-VO auswählen</option>
                {Array.isArray(mainvos) && mainvos.map(mvos => (
                <option key={mvos.main_vo_id} value={mvos.main_vo_id}>
                  {mvos.company_name}
                </option>
                ))}
              </Form.Control>
              <Form.Control.Feedback type="invalid">{errors.main_vo_id}</Form.Control.Feedback>
            </Form.Group>
            )}
            <Button variant="primary" type="submit" className="mt-3" disabled={loading}>
              {loading ? 'Wird gespeichert...' : 'Wiederverkäufer speichern'}
            </Button>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Edit Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Wiederverkäufer bearbeiten</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleEditSubmit}>
            <Form.Group className="mb-2" controlId="formCompanyNameEdit">
              <Form.Label>Firmenname</Form.Label>
              <Form.Control type="text" placeholder="Firmenname eingeben" name="company_name" value={formData.company_name} onChange={handleChange} onBlur={handleBlur} isInvalid={!!errors.company_name} />
              <Form.Control.Feedback type="invalid">{errors.company_name}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-2" controlId="formCompanyMobileEdit">
              <Form.Label>Firmen-Mobilnummer</Form.Label>
              <Form.Control type="text" placeholder="Mobilnummer der Firma eingeben" name="company_mobile_number" value={formData.company_mobile_number} onChange={handleChange} onBlur={handleBlur} isInvalid={!!errors.company_mobile_number} />
              <Form.Control.Feedback type="invalid">{errors.company_mobile_number}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-2" controlId="formCompanyEmailEdit">
              <Form.Label>Firmen-E-Mail(Benutzername)</Form.Label>
              <Form.Control type="email" placeholder="E-Mail der Firma eingeben" name="company_email" value={formData.company_email} onChange={handleChange} onBlur={handleBlur} isInvalid={!!errors.company_email} />
              <Form.Control.Feedback type="invalid">{errors.company_email}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-2" controlId="formContactFirstNameEdit">
              <Form.Label>Kontakt Vorname</Form.Label>
              <Form.Control type="text" placeholder="Vornamen des Kontakts eingeben" name="contact_first_name" value={formData.contact_first_name} onChange={handleChange} onBlur={handleBlur} isInvalid={!!errors.contact_first_name} />
              <Form.Control.Feedback type="invalid">{errors.contact_first_name}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-2" controlId="formContactLastNameEdit">
              <Form.Label>Kontakt Nachname</Form.Label>
              <Form.Control type="text" placeholder="Nachnamen des Kontakts eingeben" name="contact_last_name" value={formData.contact_last_name} onChange={handleChange} onBlur={handleBlur} isInvalid={!!errors.contact_last_name} />
              <Form.Control.Feedback type="invalid">{errors.contact_last_name}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-2" controlId="formContactMobileEdit">
              <Form.Label>Kontakt Mobilnummer</Form.Label>
              <Form.Control type="text" placeholder="Mobilnummer des Kontakts eingeben" name="contact_mobile_number" value={formData.contact_mobile_number} onChange={handleChange} onBlur={handleBlur} isInvalid={!!errors.contact_mobile_number} />
              <Form.Control.Feedback type="invalid">{errors.contact_mobile_number}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-2" controlId="formContactEmailEdit">
              <Form.Label>Kontakt E-Mail</Form.Label>
              <Form.Control type="email" placeholder="E-Mail des Kontakts eingeben" name="contact_email" value={formData.contact_email} onChange={handleChange} onBlur={handleBlur} isInvalid={!!errors.contact_email} />
              <Form.Control.Feedback type="invalid">{errors.contact_email}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-2" controlId="formStreetEdit">
              <Form.Label>Straße</Form.Label>
              <Form.Control type="text" placeholder="Straße eingeben" name="street" value={formData.street} onChange={handleChange} onBlur={handleBlur} isInvalid={!!errors.street} />
              <Form.Control.Feedback type="invalid">{errors.street}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-2" controlId="formStreetNumberEdit">
              <Form.Label>Hausnummer</Form.Label>
              <Form.Control type="text" placeholder="Hausnummer eingeben" name="street_number" value={formData.street_number} onChange={handleChange} onBlur={handleBlur} isInvalid={!!errors.street_number} />
              <Form.Control.Feedback type="invalid">{errors.street_number}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-2" controlId="formStreetAddEdit">
              <Form.Label>Straßenzusatz</Form.Label>
              <Form.Control type="text" placeholder="Straßenzusatz eingeben" name="street_add" value={formData.street_add} onChange={handleChange} onBlur={handleBlur} isInvalid={!!errors.street_add} />
              <Form.Control.Feedback type="invalid">{errors.street_add}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-2" controlId="formCityEdit">
              <Form.Label>Stadt</Form.Label>
              <Form.Control type="text" placeholder="Stadt eingeben" name="city" value={formData.city} onChange={handleChange} onBlur={handleBlur} isInvalid={!!errors.city} />
              <Form.Control.Feedback type="invalid">{errors.city}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-2" controlId="formCityAddEdit">
              <Form.Label>Stadt Zusatz</Form.Label>
              <Form.Control type="text" placeholder="Stadt Zusatz eingeben" name="city_add" value={formData.city_add} onChange={handleChange} onBlur={handleBlur} isInvalid={!!errors.city_add} />
              <Form.Control.Feedback type="invalid">{errors.city_add}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-2" controlId="formPostalCodeEdit">
              <Form.Label>PLZ</Form.Label>
              <Form.Control type="text" placeholder="PLZ eingeben" name="postal_code" value={formData.postal_code} onChange={handleChange} onBlur={handleBlur} isInvalid={!!errors.postal_code} />
              <Form.Control.Feedback type="invalid">{errors.postal_code}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-2" controlId="formStateEdit">
              <Form.Label>Bundesland</Form.Label>
              <Form.Control type="text" placeholder="Bundesland eingeben" name="state" value={formData.state} onChange={handleChange} onBlur={handleBlur} isInvalid={!!errors.state} />
              <Form.Control.Feedback type="invalid">{errors.state}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-2" controlId="formCountryEdit">
              <Form.Label>Land</Form.Label>
              <Form.Control type="text" placeholder="Land eingeben" name="country" value={formData.country} onChange={handleChange} onBlur={handleBlur} isInvalid={!!errors.country} />
              <Form.Control.Feedback type="invalid">{errors.country}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-2" controlId="formBankNameEdit">
              <Form.Label>Bankname</Form.Label>
              <Form.Control type="text" placeholder="Bankname eingeben" name="bank_name" value={formData.bank_name} onChange={handleChange} onBlur={handleBlur} isInvalid={!!errors.bank_name} />
              <Form.Control.Feedback type="invalid">{errors.bank_name}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-2" controlId="formBankAccountNumberEdit">
              <Form.Label>Kontonummer</Form.Label>
              <Form.Control type="text" placeholder="Kontonummer eingeben" name="bank_account_number" value={formData.bank_account_number} onChange={handleChange} onBlur={handleBlur} isInvalid={!!errors.bank_account_number} />
              <Form.Control.Feedback type="invalid">{errors.bank_account_number}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-2" controlId="formBankSwiftCodeEdit">
              <Form.Label>SWIFT-Code</Form.Label>
              <Form.Control type="text" placeholder="SWIFT-Code eingeben" name="bank_swift_code" value={formData.bank_swift_code} onChange={handleChange} onBlur={handleBlur} isInvalid={!!errors.bank_swift_code} />
              <Form.Control.Feedback type="invalid">{errors.bank_swift_code}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-2" controlId="formBankIBANEdit">
              <Form.Label>IBAN</Form.Label>
              <Form.Control type="text" placeholder="IBAN eingeben" name="bank_iban" value={formData.bank_iban} onChange={handleChange} onBlur={handleBlur} isInvalid={!!errors.bank_iban} />
              <Form.Control.Feedback type="invalid">{errors.bank_iban}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-2" controlId="formBankAddressEdit">
              <Form.Label>Bankadresse</Form.Label>
              <Form.Control type="text" placeholder="Bankadresse eingeben" name="bank_address" value={formData.bank_address} onChange={handleChange} onBlur={handleBlur} isInvalid={!!errors.bank_address} />
              <Form.Control.Feedback type="invalid">{errors.bank_address}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-2" controlId="formCompanyRegistryEdit">
              <Form.Label>Handelsregister</Form.Label>
              <Form.Control type="text" placeholder="Handelsregister eingeben" name="company_registry" value={formData.company_registry} onChange={handleChange} onBlur={handleBlur} isInvalid={!!errors.company_registry} />
              <Form.Control.Feedback type="invalid">{errors.company_registry}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-2" controlId="formCompanyCourtEdit">
              <Form.Label>Registergericht</Form.Label>
              <Form.Control type="text" placeholder="Registergericht eingeben" name="company_court" value={formData.company_court} onChange={handleChange} onBlur={handleBlur} isInvalid={!!errors.company_court} />
              <Form.Control.Feedback type="invalid">{errors.company_court}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-2" controlId="formCompanyCEOEdit">
              <Form.Label>Geschäftsführer</Form.Label>
              <Form.Control type="text" placeholder="Geschäftsführer eingeben" name="company_ceo" value={formData.company_ceo} onChange={handleChange} onBlur={handleBlur} isInvalid={!!errors.company_ceo} />
              <Form.Control.Feedback type="invalid">{errors.company_ceo}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-2" controlId="formCompanyTaxNumberEdit">
              <Form.Label>Steuernummer</Form.Label>
              <Form.Control type="text" placeholder="Steuernummer eingeben" name="company_tax_number" value={formData.company_tax_number} onChange={handleChange} onBlur={handleBlur} isInvalid={!!errors.company_tax_number} />
              <Form.Control.Feedback type="invalid">{errors.company_tax_number}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-2" controlId="formCompanyVATIDEdit">
              <Form.Label>Umsatzsteuer-ID</Form.Label>
              <Form.Control type="text" placeholder="Umsatzsteuer-ID eingeben" name="company_vat_id" value={formData.company_vat_id} onChange={handleChange} onBlur={handleBlur} isInvalid={!!errors.company_vat_id} />
              <Form.Control.Feedback type="invalid">{errors.company_vat_id}</Form.Control.Feedback>
            </Form.Group>
            {currentUser?.roles?.includes("ROLE_ADMIN") && (
            <Form.Group className="mb-2" controlId="formSubVoEdit">
              <Form.Label>Haupt-VO auswählen</Form.Label>
              <Form.Control as="select" name="main_vo_id" value={formData.t_main_vo.main_vo_id} onChange={handleChange} onBlur={handleBlur} isInvalid={!!errors.main_vo_id}>
                <option value="">Haupt-VO auswählen</option>
                {Array.isArray(mainvos) && mainvos.map(mvos => (
                <option key={mvos.main_vo_id} value={mvos.main_vo_id}>
                  {mvos.company_name}
                </option>
                ))}
              </Form.Control>
              <Form.Control.Feedback type="invalid">{errors.main_vo_id}</Form.Control.Feedback>
            </Form.Group>
            )}
            <Button variant="primary" type="submit" className="mt-3" disabled={loading}>
              {loading ? 'Wird aktualisiert...' : 'Aktualisieren'}
            </Button>

            <Button variant="outline-danger" className="mt-3 ms-2" onClick={handleResetPassword} disabled={loading}>
              Neues Passwort erstellen
            </Button>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Details Modal */}
      <Modal show={showDetailsModal} onHide={handleCloseDetailsModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Details für: {selectedVoForDetails?.company_name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedVoForDetails ? (
            <div>
              <p><strong>ID:</strong> {selectedVoForDetails.sub_vo_id}</p>
              <p><strong>Haupt-VO:</strong> {selectedVoForDetails.main_vo ? `${selectedVoForDetails.main_vo.main_vo_id} - ${selectedVoForDetails.main_vo.company_name}` : 'Keine Haupt-VO zugewiesen'}</p>
              <hr />
              <h5>Kontaktdaten</h5>
              <p><strong>Firmen-Mobil:</strong> {selectedVoForDetails.company_mobile_number}</p>
              <p><strong>Firmen-E-Mail(Benutzername):</strong> {selectedVoForDetails.company_email}</p>
              <p><strong>Ansprechpartner:</strong> {`${selectedVoForDetails.contact_first_name || ''} ${selectedVoForDetails.contact_last_name || ''}`}</p>
              <p><strong>Kontakt Mobil:</strong> {selectedVoForDetails.contact_mobile_number}</p>
              <p><strong>Kontakt E-Mail:</strong> {selectedVoForDetails.contact_email}</p>
              <hr />
              <h5>Adresse</h5>
              <p><strong>Straße:</strong> {selectedVoForDetails.street} {selectedVoForDetails.street_number}</p>
              <p><strong>PLZ:</strong> {selectedVoForDetails.postal_code}</p>
              <p><strong>Stadt:</strong> {selectedVoForDetails.city}</p>
              <p><strong>Bundesland:</strong> {selectedVoForDetails.state}</p>
              <p><strong>Land:</strong> {selectedVoForDetails.country}</p>
              <hr />
              <h5>Bankverbindung</h5>
              <p><strong>Bankname:</strong> {selectedVoForDetails.bank_name}</p>
              <p><strong>IBAN:</strong> {selectedVoForDetails.bank_iban}</p>
              <p><strong>SWIFT-Code:</strong> {selectedVoForDetails.bank_swift_code}</p>
              <p><strong>Kontonummer:</strong> {selectedVoForDetails.bank_account_number}</p>
              <p><strong>Bankadresse:</strong> {selectedVoForDetails.bank_address}</p>
              <hr/>
              <p><strong>Registrierungsdatum:</strong> {selectedVoForDetails.rec_date ? new Date(selectedVoForDetails.rec_date).toLocaleDateString('de-DE') : 'Nicht angegeben'}</p>
            </div>
          ) : (
            <p>Keine Details zum Anzeigen ausgewählt.</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseDetailsModal}>
            Schließen
          </Button>
        </Modal.Footer>
      </Modal>

    </Container>
  );
};

export default BoardSubVos;